import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import PageHeader from '../../components/layout/PageHeader';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import PhoneInput, { validatePhone } from '../../components/ui/PhoneInput';
import FieldAnswerList from '../../components/fields/FieldAnswerList';
import Select from '../../components/ui/Select';
import DatePicker from '../../components/ui/DatePicker';
import Textarea from '../../components/ui/Textarea';
import { listWorkflowCategories, listDocumentTypes } from '../../api/workflow';
import { listLeadSources, listNationalities } from '../../api/masters';
import { createLead, uploadLeadDocument } from '../../api/leads';
import { pushToast } from '../../components/ui/toastBus';
import { OptionCards, VEHICLE_OPTIONS, LOCATION_OPTIONS } from '../../components/candidates/OptionCards';

const EMPTY_PROFILE = {
  full_name: '',
  email: '',
  contact_no: '',
  whatsapp_no: '',
  home_country_contact_no: '',
  nationality_id: '',
  date_of_birth: '',
  passport_no: '',
  passport_expiry_date: '',
  passport_expiry_overridden: false,
  old_emirates_id_no: '',
  has_rta_permit: false,
  rta_permit_details: '',
  visited_uae_before: false,
  old_visa: '',
  old_visa_remark: '',
  visa_type: '',
  visa_validity: '',
  uid_no: '',
  uae_license_no: '',
  uae_license_expiry: '',
  home_license_no: '',
  home_license_expiry: '',
  lead_source_id: '',
  notes: '',
};

// A document conditioned on a registration field only applies once that field is answered with the option
// (walking up through conditional parents, like the server's DynamicFields::isDependencySatisfied).
function documentApplies(doc, fields, answers) {
  const dep = doc.depends_on;
  if (!dep) return true;
  const parent = fields.find((f) => f.id === dep.field_id);
  if (!parent || (parent.depends_on && !documentApplies({ depends_on: parent.depends_on }, fields, answers))) return false;
  const answer = answers[dep.field_id];
  return (Array.isArray(answer) ? answer : [answer]).includes(dep.value);
}

export default function LeadRegister() {
  const navigate = useNavigate();
  const categoriesQuery = useQuery({ queryKey: ['workflow-categories'], queryFn: listWorkflowCategories });
  const documentTypesQuery = useQuery({ queryKey: ['document-types'], queryFn: listDocumentTypes });
  const sourcesQuery = useQuery({ queryKey: ['lead-sources'], queryFn: () => listLeadSources() });
  const nationalitiesQuery = useQuery({ queryKey: ['nationalities'], queryFn: () => listNationalities() });

  const [vehicleType, setVehicleType] = useState(null);
  const [location, setLocation] = useState(null);
  const [licenseType, setLicenseType] = useState('uae');
  const [profile, setProfile] = useState(EMPTY_PROFILE);
  const [nationalityAnswers, setNationalityAnswers] = useState({});
  const [registrationAnswers, setRegistrationAnswers] = useState({});
  const [errors, setErrors] = useState({});
  const [documentFiles, setDocumentFiles] = useState({});
  const [uploading, setUploading] = useState(false);
  const [photoFile, setPhotoFile] = useState(null);
  const photoPreview = useMemo(() => (photoFile ? URL.createObjectURL(photoFile) : null), [photoFile]);

  const category = useMemo(
    () =>
      categoriesQuery.data?.find(
        (c) => c.vehicle_type === vehicleType && c.location === location && c.license_type === licenseType
      ),
    [categoriesQuery.data, vehicleType, location, licenseType]
  );

  // The registration step's own custom fields (those mirroring a fixed field are already inputs above).
  const registrationFields = (category?.steps?.find((s) => s.code === 'registration')?.fields || []).filter((f) => !f.linked_registration_field);
  const registrationStep = category?.steps?.find((s) => s.code === 'registration');
  const registrationRequiredDocs = (registrationStep?.required_documents || [])
    .filter((d) => d.required !== false && documentApplies(d, registrationStep?.fields || [], registrationAnswers))
    .map((d) => d.code);
  const selectedNationality = (nationalitiesQuery.data || []).find((n) => String(n.id) === String(profile.nationality_id));
  const nationalityFields = selectedNationality?.fields || [];
  const nationalityDocs = (selectedNationality?.documents || []).filter((d) => d.required !== false).map((d) => d.code);
  // Registration-step docs first, then any this nationality adds that aren't already listed.
  const requiredDocCodes = [...new Set([...registrationRequiredDocs, ...nationalityDocs])];
  const docTypeName = (code) => documentTypesQuery.data?.find((t) => t.code === code)?.name || code;

  // Field ids differ per workflow, so answers can't follow a change of category.
  useEffect(() => {
    setRegistrationAnswers({});
  }, [category?.id]);

  const passportWarning =
    profile.passport_expiry_date &&
    new Date(profile.passport_expiry_date) < new Date(Date.now() + 365 * 24 * 60 * 60 * 1000);

  const mutation = useMutation({
    mutationFn: (payload) => {
      if (!photoFile) return createLead(payload);
      const formData = new FormData();
      const { nationality_fields: nationalityFieldAnswers, registration_answers: registrationFieldAnswers, ...flat } = payload;
      Object.entries(flat).forEach(([k, v]) => formData.append(k, typeof v === 'boolean' ? (v ? 1 : 0) : v ?? ''));
      [
        ['nationality_fields', nationalityFieldAnswers],
        ['registration_answers', registrationFieldAnswers],
      ].forEach(([name, answers]) =>
        Object.entries(answers || {}).forEach(([id, v]) => {
          if (Array.isArray(v)) v.forEach((item) => formData.append(`${name}[${id}][]`, item));
          else formData.append(`${name}[${id}]`, typeof v === 'boolean' ? (v ? 1 : 0) : v ?? '');
        })
      );
      formData.append('profile_photo', photoFile);
      return createLead(formData);
    },
    onSuccess: async (lead) => {
      const filesToUpload = Object.entries(documentFiles).filter(([code, file]) => file && requiredDocCodes.includes(code));

      if (filesToUpload.length > 0) {
        setUploading(true);
        for (const [code, file] of filesToUpload) {
          const docType = documentTypesQuery.data?.find((t) => t.code === code);
          if (!docType) continue;

          const formData = new FormData();
          formData.append('file', file);
          formData.append('document_type_id', docType.id);

          try {
            await uploadLeadDocument(lead.id, formData);
          } catch (err) {
            const reason = err.response?.data?.message || err.response?.data?.errors?.file?.[0];
            pushToast({
              type: 'error',
              message: `Could not upload ${docType.name}${reason ? `: ${reason}` : ''}. You can add it later from the lead's profile.`,
            });
          }
        }
        setUploading(false);
      }

      pushToast({ type: 'success', message: `Lead registered: ${lead.reference_no}` });
      navigate(`/leads/${lead.id}`);
    },
    onError: (err) => setErrors(err.response?.data?.errors || {}),
  });

  const set = (field) => (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setProfile((p) => ({ ...p, [field]: value }));
  };

  const setPhone = (field) => (value) => setProfile((p) => ({ ...p, [field]: value }));

  const onNationalityChange = (e) => {
    setProfile((p) => ({ ...p, nationality_id: e.target.value }));
    setNationalityAnswers({});
    // Drop any picked files that belonged to the previous nationality's document list.
    setDocumentFiles((prev) => Object.fromEntries(Object.entries(prev).filter(([code]) => registrationRequiredDocs.includes(code))));
  };

  const setDocumentFile = (code) => (e) => {
    setDocumentFiles((prev) => ({ ...prev, [code]: e.target.files[0] || null }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!category) return;
    const phoneErrors = {};
    ['contact_no', 'whatsapp_no', 'home_country_contact_no'].forEach((f) => {
      const msg = validatePhone(profile[f]);
      if (msg) phoneErrors[f] = [msg];
    });
    if (Object.keys(phoneErrors).length) {
      setErrors(phoneErrors);
      return;
    }
    const missingDocs = requiredDocCodes.filter((code) => !documentFiles[code]);
    if (missingDocs.length > 0) {
      pushToast({ type: 'error', message: `Attach the required documents: ${missingDocs.map(docTypeName).join(', ')}.` });
      return;
    }
    const payload = { ...profile, workflow_category_id: category.id };
    if (!payload.lead_source_id) delete payload.lead_source_id;
    if (!payload.visited_uae_before) payload.old_emirates_id_no = '';
    if (nationalityFields.length > 0) payload.nationality_fields = nationalityAnswers;
    payload.registration_answers = registrationAnswers;
    if (location !== 'inside_uae') {
      delete payload.visa_type;
      delete payload.visa_validity;
      delete payload.uid_no;
    }
    mutation.mutate(payload);
  };

  const fieldError = (field) => errors[field]?.[0];

  return (
    <div className="w-full">
      <PageHeader title="Register Lead" description="Capture a prospect now — full profile details can be filled in later, before sending to Candidate." />

      <Card>
        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <h3 className="mb-3 text-sm font-semibold text-gray-900">Category</h3>
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
              <div>
                <h4 className="mb-2 text-xs font-medium uppercase tracking-wide text-gray-500">Vehicle Type</h4>
                <OptionCards options={VEHICLE_OPTIONS} value={vehicleType} onChange={setVehicleType} />
              </div>
              <div>
                <h4 className="mb-2 text-xs font-medium uppercase tracking-wide text-gray-500">Location</h4>
                <OptionCards options={LOCATION_OPTIONS} value={location} onChange={setLocation} />
              </div>
            </div>

            {vehicleType && location && licenseType && !category && (
              <p className="mt-4 text-sm text-red-600">No matching workflow category found.</p>
            )}
          </div>

          <div className="border-t border-gray-100 pt-4">
            <h3 className="mb-3 text-sm font-semibold text-gray-900">Lead Details</h3>
            <div className="mb-4 flex items-center gap-4">
              <div className="flex h-20 w-16 shrink-0 items-center justify-center overflow-hidden rounded-md border border-gray-200 bg-gray-100 text-gray-400">
                {photoPreview ? (
                  <img src={photoPreview} alt="Profile preview" className="h-full w-full object-cover" />
                ) : (
                  <svg viewBox="0 0 24 24" fill="currentColor" className="h-3/5 w-3/5" aria-hidden="true">
                    <path d="M12 12a4.5 4.5 0 1 0 0-9 4.5 4.5 0 0 0 0 9Zm0 2c-4.14 0-7.5 2.24-7.5 5v1.5h15V19c0-2.76-3.36-5-7.5-5Z" />
                  </svg>
                )}
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Profile Photo</label>
                <input
                  type="file"
                  accept=".jpg,.jpeg,.png,.webp,.heic,.heif"
                  onChange={(e) => setPhotoFile(e.target.files[0] || null)}
                  className="block w-full text-sm text-gray-600 file:mr-3 file:rounded-md file:border-0 file:bg-primary-50 file:px-3 file:py-2 file:text-sm file:font-medium file:text-primary hover:file:bg-primary-light"
                />
                {fieldError('profile_photo') && <p className="mt-1 text-xs text-red-600">{fieldError('profile_photo')}</p>}
              </div>
            </div>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
              <Input label="Full Name" required value={profile.full_name} onChange={set('full_name')} error={fieldError('full_name')} className="md:col-span-2" />
              <PhoneInput label="Contact No" required value={profile.contact_no} onChange={setPhone('contact_no')} error={fieldError('contact_no')} />
              <Select label="Source" value={profile.lead_source_id} onChange={set('lead_source_id')} error={fieldError('lead_source_id')}>
                <option value="">Select…</option>
                {(sourcesQuery.data || []).map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </Select>
              <Input label="Email" type="email" value={profile.email} onChange={set('email')} error={fieldError('email')} />
              <PhoneInput label="WhatsApp No" value={profile.whatsapp_no} onChange={setPhone('whatsapp_no')} error={fieldError('whatsapp_no')} />
              <PhoneInput label="Home Country / Emergency Contact No" value={profile.home_country_contact_no} onChange={setPhone('home_country_contact_no')} error={fieldError('home_country_contact_no')} />
              <Select label="Nationality" required value={profile.nationality_id} onChange={onNationalityChange} error={fieldError('nationality_id')}>
                <option value="">Select…</option>
                {(nationalitiesQuery.data || []).map((n) => (
                  <option key={n.id} value={n.id}>
                    {n.name}
                  </option>
                ))}
              </Select>
              <DatePicker label="Date of Birth" required value={profile.date_of_birth} onChange={set('date_of_birth')} error={fieldError('date_of_birth')} />
              <Input label="Passport No" required value={profile.passport_no} onChange={set('passport_no')} error={fieldError('passport_no')} />
              <div>
                <DatePicker
                  label="Passport Expiry Date"
                  required
                  value={profile.passport_expiry_date}
                  onChange={set('passport_expiry_date')}
                  error={fieldError('passport_expiry_date')}
                />
                {passportWarning && (
                  <div className="mt-2 rounded-md bg-amber-50 px-3 py-2 text-xs text-amber-800">
                    <p>Passport expires in less than 1 year — sending to Candidate will be blocked until this is resolved.</p>
                    <label className="mt-1 flex items-center gap-1.5">
                      <input type="checkbox" checked={profile.passport_expiry_overridden} onChange={set('passport_expiry_overridden')} />
                      Allow sending anyway
                    </label>
                  </div>
                )}
              </div>
            </div>
            <p className="mt-3 text-xs text-gray-500">
              Fields marked * are required to save a lead, along with the required documents. Everything must also be verified before it can be sent to Candidate.
            </p>
          </div>

          <div className="border-t border-gray-100 pt-4">
            <div className="mb-3 flex items-center gap-4">
              <h3 className="text-sm font-semibold text-gray-900">License Details</h3>
              <label className="flex items-center gap-1.5 text-sm text-gray-700">
                <input
                  type="checkbox"
                  checked={licenseType === 'gcc_home'}
                  onChange={(e) => setLicenseType(e.target.checked ? 'gcc_home' : 'uae')}
                  className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                />
                HC Licence
              </label>
            </div>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
              {licenseType === 'uae' && (
                <>
                  <Input label="UAE DL No" required value={profile.uae_license_no} onChange={set('uae_license_no')} error={fieldError('uae_license_no')} />
                  <DatePicker label="UAE DL Expiry" required value={profile.uae_license_expiry} onChange={set('uae_license_expiry')} error={fieldError('uae_license_expiry')} />
                </>
              )}
              {licenseType === 'gcc_home' && (
                <>
                  <Input label="HC (Home Country) DL No" required value={profile.home_license_no} onChange={set('home_license_no')} error={fieldError('home_license_no')} />
                  <DatePicker label="HC (Home Country) DL Expiry" required value={profile.home_license_expiry} onChange={set('home_license_expiry')} error={fieldError('home_license_expiry')} />
                </>
              )}
            </div>
          </div>

          {nationalityFields.length > 0 && (
            <div className="border-t border-gray-100 pt-4">
              <h3 className="mb-3 text-sm font-semibold text-gray-900">Nationality Details</h3>
              <FieldAnswerList
                fields={nationalityFields}
                answers={nationalityAnswers}
                onAnswer={(id, value) => setNationalityAnswers((prev) => ({ ...prev, [id]: value }))}
                errorFor={(id) => fieldError(`nationality_fields.${id}`)}
                className="grid grid-cols-1 gap-x-4 gap-y-3 md:grid-cols-3"
              />
            </div>
          )}

          {registrationFields.length > 0 && (
            <div className="border-t border-gray-100 pt-4">
              <h3 className="mb-3 text-sm font-semibold text-gray-900">Registration Details</h3>
              <FieldAnswerList
                fields={registrationFields}
                answers={registrationAnswers}
                onAnswer={(id, value) => setRegistrationAnswers((prev) => ({ ...prev, [id]: value }))}
                errorFor={(id) => fieldError(`registration_answers.${id}`)}
                className="grid grid-cols-1 gap-x-4 gap-y-3 md:grid-cols-3"
              />
            </div>
          )}

          {location === 'inside_uae' && (
            <div className="border-t border-gray-100 pt-4">
              <h3 className="mb-3 text-sm font-semibold text-gray-900">Visa Details (Inside UAE)</h3>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                <Select label="Visa Type" required value={profile.visa_type} onChange={set('visa_type')} error={fieldError('visa_type')}>
                  <option value="">Select…</option>
                  <option value="visit">Visit</option>
                  <option value="employment">Employment</option>
                </Select>
                <DatePicker label="Visa Validity" value={profile.visa_validity} onChange={set('visa_validity')} />
                <Input label="UID No" value={profile.uid_no} onChange={set('uid_no')} />
              </div>
            </div>
          )}

          <div className="border-t border-gray-100 pt-4">
            <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
              <input type="checkbox" checked={profile.has_rta_permit} onChange={set('has_rta_permit')} />
              Holds an RTA permit with Dubai companies?
            </label>
            {profile.has_rta_permit && (
              <Textarea
                className="mt-2"
                placeholder="Details"
                value={profile.rta_permit_details}
                onChange={set('rta_permit_details')}
              />
            )}
          </div>

          <div className="border-t border-gray-100 pt-4">
            <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
              <input type="checkbox" checked={profile.visited_uae_before} onChange={set('visited_uae_before')} />
              Did you visit the UAE before?
            </label>
            {profile.visited_uae_before && (
              <div className="mt-2 grid grid-cols-1 gap-3 md:grid-cols-2">
                <Input label="Old Emirates ID No" value={profile.old_emirates_id_no} onChange={set('old_emirates_id_no')} error={fieldError('old_emirates_id_no')} />
                <Input label="Old visa" placeholder="Old visa no. / type" value={profile.old_visa} onChange={set('old_visa')} error={fieldError('old_visa')} />
                <Textarea label="Remark" value={profile.old_visa_remark} onChange={set('old_visa_remark')} />
              </div>
            )}
          </div>

          <div className="border-t border-gray-100 pt-4">
            <Textarea label="Notes" placeholder="Any context worth keeping about this lead" value={profile.notes} onChange={set('notes')} />
          </div>

          {category && requiredDocCodes.length > 0 && (
            <div className="border-t border-gray-100 pt-4">
              <h3 className="mb-1 text-sm font-semibold text-gray-900">Required Documents</h3>
              <p className="mb-3 text-xs text-gray-500">
                All of these must be attached to register the lead.
              </p>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                {requiredDocCodes.map((code) => (
                  <div key={code}>
                    <label className="mb-1 block text-sm font-medium text-gray-700">{docTypeName(code)} <span className="text-red-600">*</span></label>
                    <input
                      type="file"
                      accept=".pdf,.jpg,.jpeg,.png,.heic,.heif,.webp"
                      onChange={setDocumentFile(code)}
                      className="block w-full text-sm text-gray-600 file:mr-3 file:rounded-md file:border-0 file:bg-primary-50 file:px-3 file:py-2 file:text-sm file:font-medium file:text-primary hover:file:bg-primary-light"
                    />
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="flex justify-end gap-3">
            <Button type="submit" loading={mutation.isPending || uploading} disabled={!category}>
              Register Lead
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
}
